import * as React from "react";
import { routes } from "configs";
import classNames from "classnames";
import { notification, Link, Button } from "components";
import {
  useAuth,
  usePrevious,
  useDispatch,
  useSelector,
  useTranslation,
} from "hooks";
import { useLocation, matchPath, useNavigate } from "react-router-dom";
import {
  Header,
  Main,
  Footer,
  Feedback,
  TranslateSwitcher,
} from "./components";
import { AppContext } from "./duck/context";
import { appOperations, appConstants, appHooks, appUtils } from "./duck";
import styles from "./App.module.css";

const Visitor = React.lazy(() => import("pages/visitor"));
const Teacher = React.lazy(() => import("pages/teacher"));
const Student = React.lazy(() => import("pages/student"));
const LTI = React.lazy(() => import("pages/lti"));
const Admin = React.lazy(() => import("pages/admin"));

const App: React.FC = () => {
  const { t } = useTranslation();
  const [auth, setAuth] = useAuth();
  const { pathname } = useLocation();
  const { user } = auth.data;
  const { isLoggedIn } = auth;
  const navigate = useNavigate();
  const prevUser = usePrevious(user);
  const online = useSelector((state) => state.offlineMode.isOnline);
  const prevOnline = usePrevious(online);
  const dispatch = useDispatch();
  const [isScreenRecordingGlobal, setIsScreenRecordingGlobal] =
    React.useState<boolean>(false);
  const { setPin } = React.use(AppContext);
  const isLtiPath = React.useMemo(
    () => appUtils.isLtiPage(pathname),
    [pathname],
  );
  appHooks.useScrollToTop();

  appHooks.useChameleon(auth);

  const isStudentWithAssignmentHomePage = React.useMemo(
    () => user?.profileType === "Student" && !user?.kindergartenDesign,
    [user?.kindergartenDesign, user?.profileType],
  );

  React.useEffect(() => {
    appOperations.authenticationCheck(
      setAuth,
      setPin,
      dispatch,
      navigate,
      online,
    );
  }, []);

  React.useEffect(() => {
    appOperations.updateSettings(user, prevUser);
  }, [user]);

  React.useEffect(() => {
    if (online && !prevOnline) {
      notification.createSuccess({
        title: t("offline.modeOnline"),
        content: t("offline.onlineDescription"),
      });
    }

    if (prevOnline && !online) {
      notification.createError({
        title: t("offline.mode"),
        content: t("offline.offlineDescription"),
      });
    }
  }, [online, prevOnline]);

  // appHooks.useLocationBlocker();

  if (auth.isAuthenticating) {
    return null;
  }

  if (isLoggedIn && isLtiPath) {
    return (
      <React.Suspense fallback={null}>
        <LTI />
      </React.Suspense>
    );
  }

  const renderedSurvey = user?.surveyAccess && (
    <Link
      withoutStyles
      to={{
        pathname: routes.campaignStaffAssignment,
      }}
      state={{
        fromLink: true,
      }}
      className={styles.additionalPanelItem}
    >
      <Button size="small" colorScheme="info">
        {t("survey.takASurvey")}
      </Button>
    </Link>
  );

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.login]: !isLoggedIn && !isLtiPath,
      })}
    >
      {isLoggedIn && (
        <Header
          className={styles.header}
          isStudentWithAssignmentHomePage={isStudentWithAssignmentHomePage}
          onClickNav={(event, nextPathName) => {
            if (
              isScreenRecordingGlobal &&
              !!appConstants.EXCLUDED_ROUTES.find((path) =>
                matchPath(path, nextPathName),
              )
            ) {
              setIsScreenRecordingGlobal(false);
              event?.preventDefault();
            }
          }}
        />
      )}
      <Main className={styles.main}>
        {isLoggedIn ? (
          <React.Suspense fallback={null}>
            {user.profileType === "Student" && (
              <>
                <Student
                  isStudentWithAssignmentHomePage={
                    isStudentWithAssignmentHomePage
                  }
                />
                <div className={styles.additionalPanel}>
                  {!appConstants.EXCLUDED_ROUTES_FOR_TRANSLATE.find((path) =>
                    matchPath(path, pathname),
                  ) && (
                    <TranslateSwitcher className={styles.additionalPanelItem} />
                  )}
                </div>
              </>
            )}
            {user.profileType === "Teacher" && (
              <>
                <Teacher
                  teacherDefaultHomeRoute={
                    auth.data?.school?.teacherDefaultHomeRoute
                  }
                />
                <div className={styles.additionalPanel}>
                  {renderedSurvey}
                  {!appConstants.EXCLUDED_ROUTES.find((path) =>
                    matchPath(path, pathname),
                  ) && (
                    <Feedback
                      className={styles.additionalPanelItem}
                      isScreenRecordingGlobal={isScreenRecordingGlobal}
                      setIsScreenRecordingGlobal={setIsScreenRecordingGlobal}
                    />
                  )}
                  {!appConstants.EXCLUDED_ROUTES_FOR_TRANSLATE.find((path) =>
                    matchPath(path, pathname),
                  ) && (
                    <TranslateSwitcher className={styles.additionalPanelItem} />
                  )}
                </div>
              </>
            )}
            {user.profileType === "Administrator" && (
              <>
                <Admin />
                <div className={styles.additionalPanel}>
                  {renderedSurvey}
                  {!appConstants.EXCLUDED_ROUTES.find((path) =>
                    matchPath(path, pathname),
                  ) && (
                    <Feedback
                      className={styles.additionalPanelItem}
                      isScreenRecordingGlobal={isScreenRecordingGlobal}
                      setIsScreenRecordingGlobal={setIsScreenRecordingGlobal}
                    />
                  )}
                  {!appConstants.EXCLUDED_ROUTES_FOR_TRANSLATE.find((path) =>
                    matchPath(path, pathname),
                  ) && (
                    <TranslateSwitcher className={styles.additionalPanelItem} />
                  )}
                </div>
              </>
            )}
          </React.Suspense>
        ) : (
          <React.Suspense fallback={null}>
            <Visitor />
          </React.Suspense>
        )}
      </Main>
      <Footer />
    </div>
  );
};

export default App;
